import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Dropdown,
  Flex,
  Input,
  Popover,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  DownOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FileTextOutlined,
  FilterOutlined,
  MailOutlined,
  PhoneOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import CustomPagination from './CustomPagination';
import '../styles/ParamCardView.css';

const { Text } = Typography;

function getFieldLabel(field) {
  return field.label ?? field.fieldLabel ?? field.name ?? field.fieldName ?? field.value;
}

function getFieldValue(field) {
  return field.value ?? field.fieldName ?? field.name ?? field.label;
}

function getRecordValue(record, fieldKey) {
  const value = record?.[fieldKey];
  if (value === null || value === undefined) return '-';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return Object.values(value).join(' ');
  return value;
}

const CANDIDATE_PREVIEW_FIELDS = [
  { key: 'name',        label: 'Name' },
  { key: 'designation', label: 'Designation' },
  { key: 'locationExp', label: 'Location / Exp' },
  { key: 'skills',      label: 'Skills' },
  { key: 'createdOn',   label: 'Created On' },
];

function CandidateCard({ record, fields, selected, onSelect }) {
  const navigate = useNavigate();
  const location = useLocation();

  const nameField     = fields.find((f) => f.cardRole === 'name');
  const subtitleField = fields.find((f) => f.cardRole === 'subtitle');
  const locationField = fields.find((f) => f.cardRole === 'location');
  const skillsField   = fields.find((f) => f.cardRole === 'skills');
  const expField      = fields.find((f) => f.cardRole === 'exp');
  const dateField     = fields.find((f) => f.cardRole === 'date');
  const actionField   = fields.find((f) => f.cardRole === 'action');

  const nameValue     = nameField     ? getRecordValue(record, nameField.key)     : '-';
  const subtitleValue = subtitleField ? getRecordValue(record, subtitleField.key) : '';
  const locationValue = locationField ? getRecordValue(record, locationField.key) : '';
  const expValue      = expField      ? getRecordValue(record, expField.key)      : '';
  const dateValue     = dateField     ? getRecordValue(record, dateField.key)     : '';

  const rawSkills      = skillsField ? record[skillsField.key] : [];
  const skills         = Array.isArray(rawSkills) ? rawSkills : [];
  const visibleSkills  = skills.slice(0, 3);
  const overflowSkills = skills.slice(3);

  const actionNode = actionField?.render
    ? actionField.render(getRecordValue(record, actionField.key), record)
    : null;

  function handlePreviewOpen() {
    navigate('/popup/preview', {
      state: {
        background: location,
        moduleName: 'candidate',
        record,
        fields: CANDIDATE_PREVIEW_FIELDS,
      },
    });
  }

  function handleMailOpen() {
    navigate('/popup/mail', {
      state: { background: location },
    });
  }

  return (
    <div className={`param-card${selected ? ' param-card--selected' : ''}`}>

      {/* ── Header ── */}
      <div className="param-card__header">
        <div className="param-card__header-left">
          <div className="param-card__name-row">
            <Checkbox
              checked={selected}
              onChange={onSelect}
              onClick={(e) => e.stopPropagation()}
              className="param-card__checkbox"
            />
            <EyeOutlined className="param-card__eye-icon" onClick={handlePreviewOpen} />
            <span className="param-card__name-block">
              <Text className="param-card__name">{nameValue}</Text>
              {subtitleValue && subtitleValue !== '-' && (
                <>
                  <span className="param-card__sep">·</span>
                  <Text className="param-card__subtitle">{subtitleValue}</Text>
                </>
              )}
            </span>
          </div>
          {locationValue && locationValue !== '-' && (
            <div className="param-card__location">
              <EnvironmentOutlined className="param-card__location-icon" />
              <Text className="param-card__location-text">{locationValue}</Text>
            </div>
          )}
        </div>

        <div className="param-card__header-right">
          <span className="param-card__exp-badge">
            Exp: {expValue && expValue !== '-' ? expValue : '-'}
          </span>
        </div>
      </div>

      {/* ── Body: Skills (left) + Created on (right) ── */}
      <div className="param-card__body">
        <div className="param-card__body-left">
          <Text className="param-card__section-label">Skills</Text>
          <div className="param-card__skills-row">
            {visibleSkills.length > 0
              ? visibleSkills.map((skill) => (
                  <Tag key={skill} className="sourcing-skill-tag">{skill}</Tag>
                ))
              : <Text className="param-card__empty">-</Text>
            }
            {overflowSkills.length > 0 && (
              <Popover
                trigger="click"
                placement="bottomLeft"
                overlayClassName="sourcing-skills-popover"
                content={
                  <div className="sourcing-skills-popup">
                    {overflowSkills.map((skill) => (
                      <Tag key={skill} className="sourcing-skill-tag">{skill}</Tag>
                    ))}
                  </div>
                }
              >
                <span className="sourcing-more-skills">+{overflowSkills.length}</span>
              </Popover>
            )}
          </div>
        </div>

        {dateValue && dateValue !== '-' && (
          <div className="param-card__body-right">
            <Text className="param-card__section-label param-card__section-label--right">
              Created on
            </Text>
            <Text className="param-card__date-value">{dateValue}</Text>
          </div>
        )}
      </div>

      {/* ── Divider ── */}
      <div className="param-card__divider" />

      {/* ── Footer ── */}
      <div className="param-card__footer">
        <div className="param-card__footer-left">
          <Tooltip title="Resume">
            <button type="button" className="param-card__icon-btn" aria-label="Resume">
              <FileTextOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Call">
            <button type="button" className="param-card__icon-btn" aria-label="Call">
              <PhoneOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Email">
            <button type="button" className="param-card__icon-btn" aria-label="Email" onClick={handleMailOpen}>
              <MailOutlined />
            </button>
          </Tooltip>
        </div>
        <div className="param-card__footer-right">
          {actionNode}
        </div>
      </div>

    </div>
  );
}

export default function ParamCardView({
  listName = 'List',
  moduleName = '',
  actions = [],
  fields = [],
  dataSource = [],
  loading = false,
  total,
  current,
  pageSize,
  onPageChange,
  onPageSizeChange,
  className = '',
  activeView = 'card',
  onViewChange,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 12 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);

  const isControlledPagination = typeof current === 'number' && typeof pageSize === 'number';
  const activePagination = isControlledPagination ? { current, pageSize } : pagination;

  const toggleSelect = (record) => {
    const id = record.id ?? record._id;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setSelectedRecords((prev) =>
      prev.some((r) => (r.id ?? r._id) === id)
        ? prev.filter((r) => (r.id ?? r._id) !== id)
        : [...prev, record]
    );
  };

  const cardMenuItems = actions
    .filter((action) => !action.maxSelect || selectedIds.length <= action.maxSelect)
    .map((action) => ({
      key: action.key ?? action.name,
      label: action.label ?? action.name,
      danger: action.danger ?? false,
      disabled: selectedIds.length === 0,
      onClick: () => {
        if (!action.route || selectedIds.length === 0) return;
        navigate(action.route, {
          state: {
            background: location,
            selectedIds,
            selectedRecords,
            moduleName,
            ...(action.popupState ?? {}),
          },
        });
      },
    }));

  const normalizedFields = useMemo(() => (
    fields
      .map((field) => ({
        ...field,
        key: getFieldValue(field),
        label: getFieldLabel(field),
      }))
      .filter((field) => field.key && field.label)
  ), [fields]);

  const displayTotal = typeof total === 'number' ? total : dataSource.length;

  const filteredData = useMemo(() => {
    const q = search.toLowerCase();
    if (!q || q.length < 3) return dataSource;
    return dataSource.filter((record) =>
      normalizedFields.some((field) =>
        String(getRecordValue(record, field.key)).toLowerCase().includes(q)
      )
    );
  }, [dataSource, normalizedFields, search]);

  const pagedData = useMemo(() => {
    if (isControlledPagination) return filteredData;
    const start = (activePagination.current - 1) * activePagination.pageSize;
    return filteredData.slice(start, start + activePagination.pageSize);
  }, [activePagination.current, activePagination.pageSize, filteredData, isControlledPagination]);

  return (
    <div className={`antd param-card-view ${className}`.trim()}>

      {/* ── Toolbar ── */}
      <Card>
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={6}>
            <Text className="param-card-view__tab-label">{listName}</Text>
            <span className="param-card-view__count">{displayTotal}</span>
            {selectedIds.length > 0 && actions.length > 0 && (
              <>
                <Dropdown menu={{ items: cardMenuItems }} trigger={['click']}>
                  <Button type="text" size="small" className="job-actions-button job-actions-menu-button">
                    Actions <DownOutlined className="job-actions-caret" />
                  </Button>
                </Dropdown>
                <Text className="job-selected-count">Selected ({selectedIds.length})</Text>
              </>
            )}
          </Flex>
          <Flex align="center" gap={8}>
            <Input
              prefix={<SearchOutlined className="job-search-icon" />}
              placeholder="Search by name, location, skills"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (isControlledPagination) onPageChange?.(1);
                else setPagination((p) => ({ ...p, current: 1 }));
              }}
              allowClear
              className="job-search-input"
            />
            <Tooltip title="Filter">
              <Button className="job-toolbar-icon-button" icon={<FilterOutlined />} />
            </Tooltip>
            <Tooltip title="Card view">
              <Button
                className={`job-toolbar-icon-button param-view-toggle-btn${activeView === 'card' ? ' param-view-toggle-btn--active' : ''}`}
                icon={<AppstoreOutlined />}
                onClick={() => onViewChange?.('card')}
              />
            </Tooltip>
            <Tooltip title="List view">
              <Button
                className={`job-toolbar-icon-button param-view-toggle-btn${activeView === 'list' ? ' param-view-toggle-btn--active' : ''}`}
                icon={<UnorderedListOutlined />}
                onClick={() => onViewChange?.('list')}
              />
            </Tooltip>
          </Flex>
        </Flex>
      </Card>

      {/* ── Card Grid ── */}
      <Spin spinning={loading}>
        {pagedData.length > 0 ? (
          <div className="param-card-view__grid">
            {pagedData.map((record, index) => (
              <CandidateCard
                key={record.id ?? record.key ?? index}
                record={record}
                fields={normalizedFields}
                selected={selectedIds.includes(record.id ?? record._id)}
                onSelect={() => toggleSelect(record)}
              />
            ))}
          </div>
        ) : (
          !loading && (
            <div className="param-card-view__empty">No results found</div>
          )
        )}
      </Spin>

      <CustomPagination
        current={activePagination.current}
        pageSize={activePagination.pageSize}
        total={displayTotal}
        onChange={(page) => {
          if (isControlledPagination) onPageChange?.(page);
          else setPagination((p) => ({ ...p, current: page }));
        }}
        onPageSizeChange={(size) => {
          if (isControlledPagination) onPageSizeChange?.(size);
          else setPagination({ current: 1, pageSize: size });
        }}
      />
    </div>
  );
}
